import type { CSSProperties } from 'react';
import type { AnimalType } from './BookCover.js';

interface PlushIllustrationProps {
  animal?: AnimalType;
  colors?: [string, string];
  size?: number | string;
  style?: CSSProperties;
}

const animalDefaults: Record<AnimalType, [string, string]> = {
  bear: ['#C8916A', '#8E5B3E'],
  rabbit: ['#E8D7BD', '#B89F7E'],
  dino: ['#A8C9A1', '#6B9163'],
};

export function PlushIllustration({
  animal = 'bear',
  colors,
  size = '100%',
  style,
}: PlushIllustrationProps) {
  const c = colors ?? animalDefaults[animal];
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        aspectRatio: '1',
        ...style,
      }}
    >
      {animal === 'bear' && (
        <>
          <div
            style={{
              position: 'absolute',
              left: '10%',
              top: '5%',
              width: '25%',
              height: '25%',
              borderRadius: '50%',
              background: `linear-gradient(160deg, ${c[0]}, ${c[1]})`,
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '10%',
              top: '5%',
              width: '25%',
              height: '25%',
              borderRadius: '50%',
              background: `linear-gradient(160deg, ${c[0]}, ${c[1]})`,
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: '12% 0 0 0',
              borderRadius: '50%',
              background: `linear-gradient(160deg, ${c[0]}, ${c[1]})`,
              boxShadow: 'inset -8px -12px 0 rgba(0,0,0,0.07)',
            }}
          >
            <div style={eyeLeft} />
            <div style={eyeRight} />
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '62%',
                transform: 'translateX(-50%)',
                width: '20%',
                height: '10%',
                borderRadius: '50% 50% 60% 60%',
                background: '#5b3520',
              }}
            />
          </div>
        </>
      )}
      {animal === 'rabbit' && (
        <>
          <div
            style={{
              position: 'absolute',
              left: '22%',
              top: '-15%',
              width: '16%',
              height: '45%',
              borderRadius: '50% 50% 40% 40%',
              background: `linear-gradient(160deg, ${c[0]}, ${c[1]})`,
              transform: 'rotate(-8deg)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: '20% 25% 15% 25%',
                borderRadius: '50% 50% 40% 40%',
                background: '#F4D8C8',
              }}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              right: '22%',
              top: '-15%',
              width: '16%',
              height: '45%',
              borderRadius: '50% 50% 40% 40%',
              background: `linear-gradient(160deg, ${c[0]}, ${c[1]})`,
              transform: 'rotate(8deg)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: '20% 25% 15% 25%',
                borderRadius: '50% 50% 40% 40%',
                background: '#F4D8C8',
              }}
            />
          </div>
          <div
            style={{
              position: 'absolute',
              inset: '22% 8% 0 8%',
              borderRadius: '45% 45% 50% 50%',
              background: `linear-gradient(160deg, ${c[0]}, ${c[1]})`,
              boxShadow: 'inset -8px -12px 0 rgba(0,0,0,0.07)',
            }}
          >
            <div style={{ ...eyeLeft, left: '26%' }} />
            <div style={{ ...eyeRight, right: '26%' }} />
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '55%',
                transform: 'translateX(-50%)',
                width: '12%',
                height: '8%',
                borderRadius: '50%',
                background: '#D87878',
              }}
            />
          </div>
        </>
      )}
      {animal === 'dino' && (
        <>
          <div
            style={{
              position: 'absolute',
              left: '30%',
              top: '10%',
              width: '8%',
              height: '12%',
              background: c[1],
              clipPath: 'polygon(0 100%, 50% 0, 100% 100%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '44%',
              top: '5%',
              width: '10%',
              height: '15%',
              background: c[1],
              clipPath: 'polygon(0 100%, 50% 0, 100% 100%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '30%',
              top: '10%',
              width: '8%',
              height: '12%',
              background: c[1],
              clipPath: 'polygon(0 100%, 50% 0, 100% 100%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: '15% 5% 0 5%',
              borderRadius: '50% 50% 45% 45%',
              background: `linear-gradient(160deg, ${c[0]}, ${c[1]})`,
              boxShadow: 'inset -8px -12px 0 rgba(0,0,0,0.07)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '22%',
                top: '32%',
                width: '14%',
                height: '20%',
                borderRadius: '50%',
                background: '#fff',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '30%',
                  top: '30%',
                  width: '50%',
                  height: '50%',
                  borderRadius: '50%',
                  background: '#1a0f06',
                }}
              />
            </div>
            <div
              style={{
                position: 'absolute',
                right: '22%',
                top: '32%',
                width: '14%',
                height: '20%',
                borderRadius: '50%',
                background: '#fff',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '30%',
                  top: '30%',
                  width: '50%',
                  height: '50%',
                  borderRadius: '50%',
                  background: '#1a0f06',
                }}
              />
            </div>
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '65%',
                transform: 'translateX(-50%)',
                width: '30%',
                height: '10%',
                borderRadius: '0 0 50% 50%',
                background: 'rgba(0,0,0,0.15)',
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

const eyeLeft: CSSProperties = {
  position: 'absolute',
  left: '30%',
  top: '38%',
  width: '10%',
  height: '13%',
  borderRadius: '50%',
  background: '#1a0f06',
};

const eyeRight: CSSProperties = {
  position: 'absolute',
  right: '30%',
  top: '38%',
  width: '10%',
  height: '13%',
  borderRadius: '50%',
  background: '#1a0f06',
};
